import { useContext } from 'react';
import styled from 'styled-components';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { t } from '@lingui/macro';
import { useRouter } from 'next/router';
import { Trans } from '@lingui/react';
import { Button } from '@components/ButtonV2';
import { Box, Flex } from '@components/layout/Grid';
import {
  ItemKind,
  LiveEvent,
  LiveEventPaymentKind,
  LiveEventQuery,
  PayForVoucherPayload,
} from '@gql/generated';
import { LiveEventFormInfo } from './LiveEventFormInfo';
import VoucherFormInputs from '@ecosystems/programs/buy-voucher/VoucherFormInputs';
import LanguageContext from '@lib/contexts/languageContext';
import VoucherPaymentModal from '@organisms/modals/payment/PayForVoucher';
import Icon from '@components/Icon';
import { Paragraph } from '@ecosystems/layout.register';
import { EventFlexiblePaymentAmount } from './EventFlexiblePaymentAmount';
import { UserContext } from '@lib/contexts/UserProvider';
import { LiveGuestAuthSection } from './LiveGuestAuthSection';

const Separator = styled.div`
  height: 1px;
  width: 100%;
  background-color: rgba(0, 0, 0, 0.15);
`;

const validationSchema = ({ isFlexiblePrice, minPrice, maxPrice }) => {
  const shape = {
    senderName: Yup.string()
      .required(t`validation.required`)
      .max(25, t`validation.firstNameMax`),
    recipientName: Yup.string()
      .required(t`validation.required`)
      .max(25, t`validation.firstNameMax`),
    recipientEmail: Yup.string()
      .required(t`validation.required`)
      .email(t`validation.email`)
      .max(50, t`validation.email`),
  };

  return Yup.object().shape(shape);
};

type RenderPayButtonProps = {
  amount: number;
  currency: string;
  values: Record<string, any>;
  liveEvent: Pick<LiveEvent, 'id' | 'title' | 'slug' | 'price'>;
  disabled?: boolean;
  onPaymentCompleted(arg0: PayForVoucherPayload): void;
};

const PayButton = ({
  amount,
  currency,
  liveEvent,
  values,
  disabled = false,
  onPaymentCompleted,
}: RenderPayButtonProps) => {
  const lang = useContext(LanguageContext);

  return (
    <VoucherPaymentModal
      senderName={values.senderName}
      recipientName={values.recipientName}
      recipientEmail={values.recipientEmail}
      customMessage={values.customMessage}
      sendGiftEmail={values.sendGiftEmail}
      id={liveEvent.id}
      slug={liveEvent.slug}
      title={liveEvent?.title}
      successPageURL={`${process.env.DOMAIN}/${lang}/live/${liveEvent.slug}/buy-gift`}
      failPageURL={`${process.env.DOMAIN}/${lang}/me/dashboard`}
      itemKind={ItemKind['LiveEvent']}
      currency={currency}
      amount={amount}
      onPaymentComplete={onPaymentCompleted}
    >
      {(open) => {
        return (
          <Button
            type="button"
            appearance="blue"
            disabled={disabled}
            onClick={() => {
              !disabled && open();
            }}
          >
            <Icon name="ygb-icon-programlock-ic" fontSize="16px" />
            &nbsp; <Trans id="live.payment_page.payment_reserve_spot" />
          </Button>
        );
      }}
    </VoucherPaymentModal>
  );
};

type Props = {
  event: LiveEventQuery['liveEvent'];
};

export const BuyGiftForm = (props: Props) => {
  const { event } = props;
  const { price } = event;
  let price_display = null;
  const router = useRouter();
  const [currentUser] = useContext(UserContext);
  const lang = router.query.lang as string;

  const minPrice =
    event?.flexibleMinAmountCents / 100 < 10
      ? 10
      : event?.flexibleMinAmountCents / 100;

  return (
    <>
      <LiveEventFormInfo event={props.event} />
      {currentUser?.id ? (
        <Formik
          validationSchema={validationSchema({
            isFlexiblePrice:
              event?.paymentKind === LiveEventPaymentKind['Flexible'],
            minPrice: minPrice < 10 ? 10 : minPrice,
            maxPrice: event?.flexibleMaxAmountCents / 100,
          })}
          initialValues={{
            senderName: '',
            recipientName: '',
            recipientEmail: '',
            sendGiftEmail: true,
            customMessage: '',
            flexiblePrice: null,
          }}
          onSubmit={() => {
            //
          }}
        >
          {(params) => {
            const {
              values,
              errors,
              setFieldValue,
              handleChange,
              ...formParams
            } = params;

            const amountToPay =
              event?.paymentKind === LiveEventPaymentKind['Flexible']
                ? values.flexiblePrice
                : price?.originalPrice / 100;

            if (event.paymentKind === LiveEventPaymentKind['Flexible']) {
              //
              price_display = (
                <Flex flexDirection="column" mb={20}>
                  <EventFlexiblePaymentAmount
                    currency={event?.price?.currency}
                    flexibleMinAmountCents={
                      // keep in at 10SEK
                      event?.flexibleMinAmountCents / 100 >= 10
                        ? event?.flexibleMinAmountCents / 100
                        : 10 * 100
                    }
                    flexibleMaxAmountCents={event?.flexibleMaxAmountCents}
                    inputName="flexiblePrice"
                    flexiblePrice={values.flexiblePrice}
                  />
                  {values.flexiblePrice ? (
                    <Flex
                      flexWrap="wrap"
                      justifyContent="space-between"
                      mt={20}
                    >
                      <Paragraph size={16} mb={0}>
                        <Trans id="live.payment_page.payment_amount" />:
                      </Paragraph>
                      <Flex>
                        <Paragraph size={18} weight={500} mb={0}>
                          {values.flexiblePrice + ' ' + price?.currency}
                        </Paragraph>
                      </Flex>
                    </Flex>
                  ) : null}
                </Flex>
              );
            } else {
              price_display = (
                <Flex flexWrap="wrap" justifyContent="space-between">
                  <Paragraph size={16} mb={0}>
                    <Trans id="live.payment_page.payment_amount" />:
                  </Paragraph>
                  <Flex>
                    <Paragraph
                      size={18}
                      weight={500}
                      mb={0}
                      // crossed={price?.discountedPrice > 0}
                    >
                      {(price?.originalPrice / 100).toFixed(2)}{' '}
                      {price?.currency}
                    </Paragraph>
                    {/* {price?.discountApplied && price?.discountedPrice ? (
                      <Paragraph size={18} weight={500} mb={0} ml={20}>
                        {(price?.discountedPrice / 100).toFixed(2)}{' '}
                        {price?.currency}
                      </Paragraph>
                    ) : null} */}
                  </Flex>
                </Flex>
              );
            }

            return (
              <form onBlur={formParams.handleBlur} onChange={handleChange}>
                <VoucherFormInputs
                  formParams={params}
                  values={values}
                  translations={{
                    'buy_gift.buyer_name.input_label': t`programs_2021.buy_gift.buyer_name.input_label`,
                    'buy_gift.buyer_name.input_placeholder': t`programs_2021.buy_gift.buyer_name.input_placeholder`,
                    'buy_gift.recipient_name.input_label': t`programs_2021.buy_gift.recipient_name.input_label`,
                    'buy_gift.recipient_email.input_label': t`programs_2021.buy_gift.recipient_email.input_label`,
                    'buy_gift.direct_email.checkbox_label': t`programs_2021.buy_gift.direct_email.checkbox_label`,
                    'buy_gift.direct_email.tooltip_text': t`programs_2021.buy_gift.direct_email.tooltip_text`,
                    'buy_gift.custom_message.input_label': t`programs_2021.buy_gift.custom_message.input_label`,
                  }}
                />
                <Box my={20}>
                  <Separator />
                </Box>
                <Box mt={-10} mb={20}>
                  {price_display}
                </Box>
                <PayButton
                  amount={amountToPay}
                  currency={price?.currency}
                  liveEvent={props.event}
                  values={values}
                  disabled={
                    !formParams?.isValid ||
                    !formParams?.dirty ||
                    (event?.paymentKind === LiveEventPaymentKind['Flexible'] &&
                      !(
                        event?.flexibleMaxAmountCents / 100 >=
                          values.flexiblePrice &&
                        minPrice <= values.flexiblePrice
                      ))
                  }
                  onPaymentCompleted={(data) => {
                    // this will only be called on adyen success payment
                    router.push({
                      pathname: `/${lang}/live/${event?.slug}/success`,
                      query: {
                        email: currentUser.email,
                        order_id: data?.orderId,
                      },
                    });
                  }}
                />
              </form>
            );
          }}
        </Formik>
      ) : (
        <LiveGuestAuthSection />
      )}
    </>
  );
};
